/**
 * Widget palette — the curated set of widget templates that can be added to a dashboard view.
 *
 * The templates themselves are unchanged (the backend persists `widgetType` and
 * `configurationJson` verbatim). What changed is the shell: MUI's `Grid` + `Card` + `Typography`
 * became a token-styled BEM list, because a two-line card with a button is exactly the kind of
 * hand-rolled panel the design system already answers for.
 */

import { Button } from '@coordio/ui';
import { Plus } from 'lucide-react';

import './dashboardWidgets.scss';

/**
 * Describes an addable dashboard widget template.
 */
export interface WidgetTemplate {
  /**
   * Widget type identifier persisted in the backend (e.g., MetricCard, Chart).
   */
  widgetType: string;

  /**
   * Default title for the widget.
   */
  title: string;

  /**
   * Optional description for the widget.
   */
  description?: string;

  /**
   * Default grid width (12-col grid).
   */
  defaultWidth: number;

  /**
   * Default grid height (row units; canvas rowHeight ≈ 30px).
   */
  defaultHeight: number;

  /**
   * Minimum width the user can resize down to.
   */
  minWidth: number;

  /**
   * Minimum height the user can resize down to.
   */
  minHeight: number;

  /**
   * Widget configuration JSON persisted in the backend.
   */
  configurationJson: string;
}

/**
 * Grid size defaults per widget type — used when adding widgets and when healing
 * undersized layouts loaded from the backend (older saves used tiny h/w values).
 */
export const WIDGET_SIZE_BY_TYPE: Record<
  string,
  { w: number; h: number; minW: number; minH: number }
> = {
  MetricCard: { w: 3, h: 5, minW: 2, minH: 4 },
  UsageTrends: { w: 8, h: 12, minW: 6, minH: 10 },
  CostByModel: { w: 4, h: 12, minW: 3, minH: 10 },
  RealTimeMetrics: { w: 4, h: 9, minW: 3, minH: 7 },
};

const FALLBACK_SIZE = { w: 4, h: 8, minW: 2, minH: 4 };

/** Resolves layout size constraints for a widget type. */
export const getWidgetSize = (widgetType: string) =>
  WIDGET_SIZE_BY_TYPE[widgetType] ?? FALLBACK_SIZE;

const defaultTemplates: WidgetTemplate[] = [
  {
    widgetType: 'MetricCard',
    title: 'Metric Card',
    description: 'A quick KPI card (configure which metric in JSON).',
    defaultWidth: WIDGET_SIZE_BY_TYPE.MetricCard.w,
    defaultHeight: WIDGET_SIZE_BY_TYPE.MetricCard.h,
    minWidth: WIDGET_SIZE_BY_TYPE.MetricCard.minW,
    minHeight: WIDGET_SIZE_BY_TYPE.MetricCard.minH,
    configurationJson: JSON.stringify({ metric: 'activeSessions' }),
  },
  {
    widgetType: 'UsageTrends',
    title: 'Usage Trends',
    description: 'Tokens and cost trend chart for the selected range.',
    defaultWidth: WIDGET_SIZE_BY_TYPE.UsageTrends.w,
    defaultHeight: WIDGET_SIZE_BY_TYPE.UsageTrends.h,
    minWidth: WIDGET_SIZE_BY_TYPE.UsageTrends.minW,
    minHeight: WIDGET_SIZE_BY_TYPE.UsageTrends.minH,
    configurationJson: JSON.stringify({ rangeDays: 30, chartType: 'area' }),
  },
  {
    widgetType: 'CostByModel',
    title: 'Cost by Model',
    description: 'Pie chart of cost split by model.',
    defaultWidth: WIDGET_SIZE_BY_TYPE.CostByModel.w,
    defaultHeight: WIDGET_SIZE_BY_TYPE.CostByModel.h,
    minWidth: WIDGET_SIZE_BY_TYPE.CostByModel.minW,
    minHeight: WIDGET_SIZE_BY_TYPE.CostByModel.minH,
    configurationJson: JSON.stringify({ topN: 6 }),
  },
  {
    widgetType: 'RealTimeMetrics',
    title: 'Real-time Activity',
    description: 'Live activity widget backed by SignalR.',
    defaultWidth: WIDGET_SIZE_BY_TYPE.RealTimeMetrics.w,
    defaultHeight: WIDGET_SIZE_BY_TYPE.RealTimeMetrics.h,
    minWidth: WIDGET_SIZE_BY_TYPE.RealTimeMetrics.minW,
    minHeight: WIDGET_SIZE_BY_TYPE.RealTimeMetrics.minH,
    configurationJson: JSON.stringify({}),
  },
];

export interface WidgetPaletteProps {
  /**
   * Callback invoked when the user adds a widget template.
   */
  onAdd: (template: WidgetTemplate) => void;
}

/**
 * Widget palette component.
 */
export const WidgetPalette = ({ onAdd }: WidgetPaletteProps) => {
  return (
    <div className="widget-palette" data-testid="widget-palette">
      {defaultTemplates.map((template) => (
        <div className="widget-palette__item" key={template.widgetType}>
          <span className="widget-palette__title">{template.title}</span>
          {template.description && <span className="widget-palette__description">{template.description}</span>}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => onAdd(template)}
            data-testid={`widget-palette-add-${template.widgetType}`}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Add
          </Button>
        </div>
      ))}
    </div>
  );
};
